import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for usage abort controller in React components
 * @param {Object} includes isAbortOnUnmount param for abort request on component unmount
 * @returns {Object} Returns object that contains callbacks for AbortController (WebApi)
 * NB: For passing returned object as prop use memoization.
 */

const useAbortController = ({ isAbortOnUnmount = true } = {}) => {
  const abortControllerRef = useRef();

  const getAbortSignal = useCallback(() => {
    abortControllerRef.current ??= new AbortController();

    return abortControllerRef.current.signal;
  }, []);

  const resetAbortSignal = useCallback(() => {
    abortControllerRef.current = null;
  }, []);

  const emitAbort = useCallback(reason => {
    abortControllerRef.current?.abort(reason);
  }, []);

  useEffect(() => {
    return () => {
      if (isAbortOnUnmount) {
        emitAbort();
      }
    };
  }, [emitAbort, isAbortOnUnmount]);

  return { getAbortSignal, resetAbortSignal, emitAbort };
};

export default useAbortController;
